import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	OnInit,
	inject,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UrlService } from '@services/url/url.service';
import { GenericFormsTemplatesState } from '@state/generic-forms/state';
import { AtomicModule } from 'src/app/shared/atomic/atomic.module';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { GlobalGenericFormTemplate } from '@models/global-forms';
import { filter, map, take } from 'rxjs';

const formTypeOrder = ['Land Transfer Tax', 'Sales Tax', 'E-Registration'];

@Component({
	selector: 'app-generic-forms',
	imports: [CommonModule, AtomicModule, RouterModule],
	templateUrl: './generic-forms.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GenericFormsComponent implements OnInit {
	private store = inject(Store);
	private router = inject(Router);
	private route = inject(ActivatedRoute);

	public baseRoute = UrlService.Matter_Documents_Generic_Forms(
		this.store.selectSnapshot(SelectedMatter.id),
	);

	public genericFormsByFormType$ = this.store
		.select(GenericFormsTemplatesState.mapGenericFormsByFormType)
		.pipe(
			// Sort the form types based on the predefined order
			map((genericFormsByFormType) => {
				const sortedFormTypes = Object.keys(genericFormsByFormType).sort(
					(a, b) => {
						const indexA = formTypeOrder.indexOf(a);
						const indexB = formTypeOrder.indexOf(b);
						if (indexA === -1 && indexB === -1) return 0;
						if (indexA === -1) return 1;
						if (indexB === -1) return -1;
						return indexA - indexB;
					},
				);
				return sortedFormTypes.reduce(
					(acc, formType) => {
						acc[formType] = genericFormsByFormType[formType];
						return acc;
					},
					{} as Record<string, GlobalGenericFormTemplate[]>,
				);
			}),
		);

	ngOnInit(): void {
		// Check if we're at the base route with no form ID
		if (this.router.url === this.baseRoute) {
			this.routeToFirstLandTransferTaxForm();
		}
	}

	private routeToFirstLandTransferTaxForm(): void {
		this.genericFormsByFormType$
			.pipe(
				filter(
					(forms) =>
						!!forms['Land Transfer Tax'] &&
						forms['Land Transfer Tax'].length > 0,
				),
				take(1),
			)
			.subscribe((forms) => {
				const firstForm = forms['Land Transfer Tax'][0];
				if (firstForm) {
					this.router.navigate([firstForm.id], { relativeTo: this.route });
				}
			});
	}

	public compareFn = () => 0;

	public trackByFormType = (_: number, formType: string) => formType;

	public trackByGenericForm = (
		_: number,
		genericForm: GlobalGenericFormTemplate,
	) => genericForm.id;
}
